import { StateCreator } from 'zustand';
import type { AppState } from '../../store/app-state';
import { FeedPost, FeedComment, PostType, MediaAttachment, ContentLink, Mention, FeedReaction, FeedReactionKind, FeedReactionTarget } from './types';
import {
    createFeedPost,
    updateFeedPost as apiUpdatePost,
    deleteFeedPost as apiDeletePost,
    toggleFeedPostPin as apiTogglePin,
    createFeedComment,
    deleteFeedComment as apiDeleteComment,
    setFeedReaction as apiSetReaction,
    deleteFeedReaction as apiDeleteReaction,
} from './feed-api';
import type { ProjectScope } from '../sync/project-scope';
import {
    markPendingFeedCommentWrite,
    markPendingFeedPostWrite,
    markPendingFeedReactionWrite,
} from '../sync/feed-pending-writes';
import { feedReadReceiptKey } from './feed-read-receipts';
import type { FeedReadReceiptMap, FeedReadReceiptScope } from './feed-read-receipts';

type FeedSnapshotReadReceiptState = {
    lastViewedFeedAt: number;
    lastViewedFeedAtByScope: FeedReadReceiptMap;
};

export interface FeedSlice {
    feedPosts: FeedPost[];
    feedComments: FeedComment[];
    feedReactions: FeedReaction[];
    lastViewedFeedAt: number; // Active project feed read baseline
    // Local, per-user project receipts used to avoid treating first-sync posts as newly unread.
    lastViewedFeedAtByScope: FeedReadReceiptMap;
    feedActions: {
        addPost: (
            authorId: string,
            authorName: string,
            content: string,
            postType?: PostType,
            media?: MediaAttachment[],
            contentLinks?: ContentLink[],
            mentions?: Mention[],
            authorPhotoUrl?: string
        ) => void;
        updatePost: (id: string, updates: Partial<FeedPost>) => void;
        deletePost: (id: string) => void;
        togglePostPin: (id: string) => void;
        markFeedAsRead: () => void; // Updates lastViewedFeedAt to current time
        
        addComment: (
            postId: string,
            authorId: string,
            authorName: string,
            content: string,
            mentions?: Mention[]
        ) => void;
        deleteComment: (id: string) => void;
        setPostReaction: (postId: string, userId: string, reaction: FeedReactionKind | null) => void;
        setCommentReaction: (commentId: string, userId: string, reaction: FeedReactionKind | null) => void;

        // Store-only snapshot actions — used by useFeedSync to update from listeners
        setPosts: (posts: FeedPost[]) => void;
        setPostsWithReadReceipt: (
            posts: FeedPost[],
            readReceiptState: FeedSnapshotReadReceiptState
        ) => void;
        setComments: (comments: FeedComment[]) => void;
        setReactions: (reactions: FeedReaction[]) => void;
        
        // Project scoping
        clearFeed: () => void;
    };
}

function getFeedProjectScope(state: AppState & FeedSlice): ProjectScope | null {
    if (!state.cloud.enabled || !state.cloud.orgId || !state.cloud.projectId) {
        return null;
    }
    return { orgId: state.cloud.orgId, projectId: state.cloud.projectId };
}

function getFeedReadReceiptScope(state: AppState & FeedSlice): FeedReadReceiptScope | null {
    const scope = getFeedProjectScope(state);
    const userId = state.auth.user?.uid;

    if (!scope || !userId) {
        return null;
    }

    return { ...scope, userId };
}

function feedReactionId(targetType: FeedReactionTarget, targetId: string, userId: string): string {
    return `${targetType}_${targetId}_${userId}`;
}

export const createFeedSlice: StateCreator<
    AppState & FeedSlice,
    [["zustand/immer", never]],
    [],
    FeedSlice
> = (set, get) => {
    const setTargetReaction = (
        targetType: FeedReactionTarget,
        targetId: string,
        userId: string,
        reaction: FeedReactionKind | null
    ) => {
        const scope = getFeedProjectScope(get());
        const id = feedReactionId(targetType, targetId, userId);
        const timestamp = Date.now();
        const existingReaction = get().feedReactions.find(item => item.id === id);

        set(state => {
            if (!state.feedReactions) state.feedReactions = [];
            const existing = state.feedReactions.find(item => item.id === id);

            if (!reaction) {
                if (existing) {
                    existing.deleted = true;
                    existing.updatedAt = timestamp;
                }
                return;
            }

            if (existing) {
                existing.reaction = reaction;
                existing.deleted = false;
                existing.updatedAt = timestamp;
                return;
            }

            state.feedReactions.push({
                id,
                targetType,
                targetId,
                userId,
                reaction,
                createdAt: timestamp,
                updatedAt: timestamp,
                deleted: false,
            });
        });

        if (!scope) return;

        if (!reaction) {
            if (existingReaction) {
                markPendingFeedReactionWrite(scope, id);
                apiDeleteReaction(scope, id, timestamp)
                    .catch(err => console.error('[feed] Failed to delete reaction:', err));
            }
            return;
        }

        markPendingFeedReactionWrite(scope, id);
        apiSetReaction(scope, {
            id,
            targetType,
            targetId,
            userId,
            reaction,
            createdAt: existingReaction?.createdAt ?? timestamp,
            updatedAt: timestamp,
        }).catch(err => console.error('[feed] Failed to set reaction:', err));
    };

    return ({
    feedPosts: [],
    feedComments: [],
    feedReactions: [],
    lastViewedFeedAt: 0,
    lastViewedFeedAtByScope: {},
    feedActions: {
        addPost: (authorId, authorName, content, postType, media, contentLinks, mentions, authorPhotoUrl) => {
            const scope = getFeedProjectScope(get());
            const id = crypto.randomUUID();
            const timestamp = Date.now();
            const resolvedPostType = postType ?? 'text';
            const resolvedMedia = media ?? [];
            const resolvedContentLinks = contentLinks ?? [];
            const resolvedMentions = mentions ?? [];
            const post: FeedPost = {
                id,
                authorId,
                authorName,
                ...(authorPhotoUrl ? { authorPhotoUrl } : {}),
                postType: resolvedPostType,
                content,
                media: resolvedMedia,
                contentLinks: resolvedContentLinks,
                mentions: resolvedMentions,
                pinned: false,
                createdAt: timestamp,
                updatedAt: timestamp,
                deleted: false
            };

            set(state => {
                if (!state.feedPosts) state.feedPosts = [];
                state.feedPosts.unshift(post);
            });

            if (scope) {
                markPendingFeedPostWrite(scope, id);
                createFeedPost(scope, {
                    id,
                    authorId,
                    authorName,
                    content,
                    postType,
                    media,
                    contentLinks,
                    mentions,
                    authorPhotoUrl,
                    createdAt: timestamp,
                    updatedAt: timestamp,
                }).catch(err => console.error('[feed] Failed to create post:', err));
            }
        },
        
        updatePost: (id, updates) => {
            const scope = getFeedProjectScope(get());
            const updatedAt = updates.updatedAt ?? Date.now();
            const optimisticUpdates = { ...updates, updatedAt };

            set(state => {
                const post = state.feedPosts.find(p => p.id === id);
                if (post) {
                    Object.assign(post, optimisticUpdates);
                }
            });

            if (scope) {
                markPendingFeedPostWrite(scope, id);
                apiUpdatePost(scope, id, optimisticUpdates)
                    .catch(err => console.error('[feed] Failed to update post:', err));
            }
        },
        
        deletePost: (id) => {
            const scope = getFeedProjectScope(get());
            const updatedAt = Date.now();

            set(state => {
                const post = state.feedPosts.find(p => p.id === id);
                if (post) {
                    post.deleted = true;
                    post.updatedAt = updatedAt;
                }
            });

            if (scope) {
                markPendingFeedPostWrite(scope, id);
                apiDeletePost(scope, id, updatedAt)
                    .catch(err => console.error('[feed] Failed to delete post:', err));
            }
        },
        
        togglePostPin: (id) => {
            const scope = getFeedProjectScope(get());
            const post = get().feedPosts.find(p => p.id === id);
            if (!post) return;

            const pinned = !post.pinned;
            const updatedAt = Date.now();
            set(state => {
                const p = state.feedPosts.find(p => p.id === id);
                if (p) {
                    p.pinned = pinned;
                    p.updatedAt = updatedAt;
                }
            });

            if (scope && post) {
                markPendingFeedPostWrite(scope, id);
                apiTogglePin(scope, id, pinned, updatedAt)
                    .catch(err => console.error('[feed] Failed to toggle pin:', err));
            }
        },
        
        markFeedAsRead: () => {
            const scope = getFeedReadReceiptScope(get());
            const timestamp = Date.now();

            set(state => {
                state.lastViewedFeedAt = timestamp;
                if (scope) {
                    if (!state.lastViewedFeedAtByScope) {
                        state.lastViewedFeedAtByScope = {};
                    }
                    state.lastViewedFeedAtByScope[feedReadReceiptKey(scope)] = timestamp;
                }
            });
        },
        
        addComment: (postId, authorId, authorName, content, mentions = []) => {
            const scope = getFeedProjectScope(get());
            const id = crypto.randomUUID();
            const timestamp = Date.now();
            const comment: FeedComment = {
                id,
                postId,
                authorId,
                authorName,
                content,
                mentions,
                createdAt: timestamp,
                deleted: false
            };

            set(state => {
                if (!state.feedComments) state.feedComments = [];
                state.feedComments.push(comment);
                // Update post updatedAt timestamp
                const post = state.feedPosts.find(p => p.id === postId);
                if (post) post.updatedAt = timestamp;
            });

            if (scope) {
                markPendingFeedCommentWrite(scope, id);
                createFeedComment(scope, {
                    id,
                    postId,
                    authorId,
                    authorName,
                    content,
                    mentions,
                    createdAt: timestamp,
                }).catch(err => console.error('[feed] Failed to create comment:', err));
            }
        },
        
        deleteComment: (id) => {
            const scope = getFeedProjectScope(get());

            set(state => {
                const comment = state.feedComments.find(c => c.id === id);
                if (comment) comment.deleted = true;
            });

            if (scope) {
                markPendingFeedCommentWrite(scope, id);
                apiDeleteComment(scope, id)
                    .catch(err => console.error('[feed] Failed to delete comment:', err));
            }
        },

        setPostReaction: (postId, userId, reaction) => {
            setTargetReaction('post', postId, userId, reaction);
        },

        setCommentReaction: (commentId, userId, reaction) => {
            setTargetReaction('comment', commentId, userId, reaction);
        },

        // Store-only snapshot actions — called by useFeedSync from listener data
        setPosts: (posts) => set(state => {
            state.feedPosts = posts;
        }),

        setPostsWithReadReceipt: (posts, readReceiptState) => set(state => {
            state.feedPosts = posts;
            state.lastViewedFeedAt = readReceiptState.lastViewedFeedAt;
            state.lastViewedFeedAtByScope = readReceiptState.lastViewedFeedAtByScope;
        }),
        
        setComments: (comments) => set(state => {
            state.feedComments = comments;
        }),

        setReactions: (reactions) => set(state => {
            state.feedReactions = reactions;
        }),
        
        clearFeed: () => set(state => {
            state.feedPosts = [];
            state.feedComments = [];
            state.feedReactions = [];
            state.lastViewedFeedAt = 0;
            if (!state.lastViewedFeedAtByScope) {
                state.lastViewedFeedAtByScope = {};
            }
        })
    }
    });
};
